/* eslint-disable */
import React, { useState, useEffect, useRef } from "react";
import { useParams, useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";

import * as url from "helper/UrlHelper";
import { getData, postData } from "utils/Gateway";

import BreadCrumb from "components/Common/BreadCrumb/BreadCrumb";
import AlertNotification from "components/Common/AlertNotification/AlertNotification";
import WbHelpOffcanvas from "../Offcanvas/WbHelpOffcanvas";

// ─── Progress messages shown while AI is generating ──────────────────────────
// Each entry: [minElapsedSeconds, message]
const PROGRESS_STAGES = [
  [0,   "Analyzing your request..."],
  [4,   "Understanding the page structure..."],
  [10,  "Generating HTML structure..."],
  [20,  "Styling the components..."],
  [35,  "Adding interactions and behaviors..."],
  [55,  "Integrating into the existing page..."],
  [80,  "Still working — complex changes take a moment..."],
  [120, "Finalizing everything, almost there..."],
  [180, "Taking longer than usual — please hold on..."],
];

const getProgressMessage = (elapsedMs) => {
  const sec = elapsedMs / 1000;
  let msg = PROGRESS_STAGES[0][1];
  for (const [threshold, text] of PROGRESS_STAGES) {
    if (sec >= threshold) msg = text;
  }
  return msg;
};

// Format milliseconds → "0:23" or "1:13" (m:ss)
const formatDuration = (ms) => {
  const totalSec = Math.round(ms / 1000);
  const m = Math.floor(totalSec / 60);
  const s = totalSec % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
};

// ─── Predefined template prompts (MODE 3) ────────────────────────────────────
const TEMPLATES = [
  { label: "Modern SaaS hero section with gradient background", icon: "rocket_launch" },
  { label: "Services grid with icons and descriptions", icon: "grid_view" },
  { label: "Team members section with profile cards", icon: "groups" },
  { label: "Testimonials carousel with star ratings", icon: "star" },
  { label: "Contact section with form and map placeholder", icon: "contact_mail" },
  { label: "Footer with links, social icons and newsletter signup", icon: "web" },
  { label: "Stats/numbers section with animated counters", icon: "bar_chart" },
  { label: "FAQ accordion section", icon: "quiz" },
];

// ─── API reference tokens ─────────────────────────────────────────────────────
const API_TOKENS = [
  { token: "API_GET_SERVICES", endpoint: "/api/services", schema: "[{ name, description, price, image }]" },
  { token: "API_GET_STAFFS", endpoint: "/api/staffs", schema: "[{ name, role, image, bio }]" },
  { token: "API_GET_REVIEWS", endpoint: "/api/reviews", schema: "[{ author, rating, comment }]" },
  { token: "API_GET_POSTS", endpoint: "/api/posts", schema: "[{ title, content, image, date }]" },
  { token: "API_GET_SUCCESS_STORIES", endpoint: "/api/success-stories", schema: "[{ title, description, image }]" },
];

// ─── Build a full HTML document for the iframe preview ───────────────────────
const buildPreviewDocument = (html, css, js) => {
  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1.0" />
  <base href="${url.SERVER_URL}/" />
  <link href="https://cdn.jsdelivr.net/npm/bootstrap@5.3.3/dist/css/bootstrap.min.css" rel="stylesheet" />
  <style>
    body { font-family: system-ui, sans-serif; }
    ${css}
  </style>
</head>
<body>
  ${html}
  <script src="https://cdn.jsdelivr.net/npm/bootstrap@5.3.3/dist/js/bootstrap.bundle.min.js"><\/script>
  <script defer>
    ${js}
  <\/script>
</body>
</html>`;
};

const PageEditorBody = () => {
  const { id } = useParams();
  const history = useHistory();
  const token = localStorage.getItem("token");
  const { t } = useTranslation();

  // ── Page data ────────────────────────────────────────────────────────────
  const [pageName, setPageName] = useState("");
  const [pageSlug, setPageSlug] = useState("");
  const [pageCompanyCif, setPageCompanyCif] = useState("");
  const [pageApiBaseUrl, setPageApiBaseUrl] = useState("");
  const [pageStatus, setPageStatus] = useState("draft");
  const [html, setHtml] = useState("");
  const [css, setCss] = useState("");
  const [js, setJs] = useState("");
  const [chatHistory, setChatHistory] = useState([]);
  const [versions, setVersions] = useState([]);

  // ── UI state ─────────────────────────────────────────────────────────────
  const [inputMode, setInputMode] = useState("chat"); // "chat" | "upload"
  const [activeRightTab, setActiveRightTab] = useState("preview"); // "preview"|"html"|"css"|"js"
  const [showApiPanel, setShowApiPanel] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false); // WYSIWYG direct edit
  const [iframeKey, setIframeKey] = useState(0); // increment to force iframe remount

  // ── Chat / generation ─────────────────────────────────────────────────────
  const [prompt, setPrompt] = useState("");
  const [selectedApiTokens, setSelectedApiTokens] = useState([]);
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatingMessage, setGeneratingMessage] = useState("");
  const generatingStartRef = useRef(null);

  // ── Upload mode ───────────────────────────────────────────────────────────
  const [uploadedFile, setUploadedFile] = useState(null);
  const [uploadedFileBase64, setUploadedFileBase64] = useState("");
  const [uploadNote, setUploadNote] = useState("");
  const [isDragging, setIsDragging] = useState(false);

  // ── Section-by-section design pipeline ───────────────────────────────────
  const [designStep, setDesignStep] = useState("upload"); // "upload"|"analyzing"|"sections"|"building"
  const [analyzedSections, setAnalyzedSections] = useState([]); // [{order,name,description,selected,status,durationMs}]
  const [buildingIndex, setBuildingIndex] = useState(-1);
  const accHtmlRef = useRef("");
  const accCssRef  = useRef("");
  const accJsRef   = useRef("");

  // ── Alerts ────────────────────────────────────────────────────────────────
  const [showAlert, setShowAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [messageType, setMessageType] = useState("");

  const chatBottomRef = useRef(null);
  const fileInputRef = useRef(null);
  const iframeRef = useRef(null);

  const breadcrumbText = [
    { title: t("Website Builder"), link: "/admin/websitebuilder/list" },
    { title: pageName || t("Editor") },
  ];

  // ── Fetch page details on mount ───────────────────────────────────────────
  const getPageDetails = async () => {
    try {
      const requestUrl =
        url.API_BASE_URL +
        url.API_GET_WEBSITE_PAGE_DETAILS +
        `/${id}?token=${token}`;

      const response = await getData(requestUrl);

      if (response.status) {
        const page = response.data;
        setPageName(page.name);
        setPageSlug(page.slug || "");
        setPageCompanyCif(page.companycif || "");
        setPageApiBaseUrl(page.apibaseurl || "");
        setPageStatus(page.status);
        setHtml(page.html || "");
        setCss(page.css || "");
        setJs(page.js || "");
        setChatHistory(page.chatHistory || []);
        setVersions(page.versions || []);
      }
    } catch (error) {
      setAlertMessage(error.message);
      setMessageType("error");
      setShowAlert(true);
    }
  };

  // ── Unified AI generation ─────────────────────────────────────────────────
  const handleGenerate = async (overridePrompt = null, overrideImage = null) => {
    const sendPrompt = overridePrompt || prompt;
    if (!sendPrompt.trim() && !overrideImage) return;

    try {
      setIsGenerating(true);

      const requestUrl =
        url.API_BASE_URL +
        url.API_GENERATE_WEBSITE_PAGE_CODE +
        `/${id}/generate?token=${token}`;

      const body = {
        prompt: sendPrompt,
        chatHistory,
        apiTokens: selectedApiTokens,
      };

      if (overrideImage) {
        body.image = overrideImage;
      }

      const response = await postData(requestUrl, body);

      if (response.status) {
        setHtml(response.data.html);
        setCss(response.data.css);
        setJs(response.data.js);
        setChatHistory(response.data.chatHistory);
        setVersions(response.data.versions);
        setPrompt("");
        setActiveRightTab("preview");
        // switch back to chat after image generation
        if (overrideImage) {
          setInputMode("chat");
          setUploadedFile(null);
          setUploadedFileBase64("");
          setUploadNote("");
        }
      } else {
        setAlertMessage(response.message || t("Generation failed"));
        setMessageType("error");
        setShowAlert(true);
      }
    } catch (error) {
      setAlertMessage(error.message);
      setMessageType("error");
      setShowAlert(true);
    } finally {
      setIsGenerating(false);
    }
  };

  // ── Prompt key handler (Enter = send, Shift+Enter = newline) ─────────────
  const handlePromptKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleGenerate();
    }
  };

  // ── Template click → populate prompt input ────────────────────────────────
  const handleTemplateSelect = (templateLabel) => {
    setPrompt(templateLabel);
  };

  // ── API token click → append to prompt ───────────────────────────────────
  const handleTokenAppend = (token) => {
    setSelectedApiTokens((prev) =>
      prev.includes(token) ? prev.filter((t) => t !== token) : [...prev, token]
    );
  };

  // ── File drag & drop ──────────────────────────────────────────────────────
  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => setIsDragging(false);

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) processFile(file);
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) processFile(file);
  };

  const processFile = (file) => {
    const ALLOWED = ["image/png", "image/jpeg", "image/jpg", "application/pdf"];
    const MAX_SIZE = 10 * 1024 * 1024; // 10MB

    if (!ALLOWED.includes(file.type)) {
      setAlertMessage(t("Only PNG, JPG, and PDF files are allowed"));
      setMessageType("error");
      setShowAlert(true);
      return;
    }

    if (file.size > MAX_SIZE) {
      setAlertMessage(t("File size must be less than 10MB"));
      setMessageType("error");
      setShowAlert(true);
      return;
    }

    setUploadedFile(file);

    const reader = new FileReader();
    reader.onload = (ev) => {
      // Send full data URL (includes MIME type prefix) so backend can detect format
      setUploadedFileBase64(ev.target.result);
    };
    reader.readAsDataURL(file);
  };

  const handleGenerateFromDesign = () => {
    if (!uploadedFileBase64) return;
    const visionPrompt =
      "Replicate this design exactly. Extract colors, layout, typography, and spacing. " +
      (uploadNote.trim() ? uploadNote.trim() : "");
    handleGenerate(visionPrompt, uploadedFileBase64);
  };

  // ── Section-by-section design pipeline ───────────────────────────────────
  const resetDesignFlow = () => {
    setDesignStep("upload");
    setAnalyzedSections([]);
    setBuildingIndex(-1);
    accHtmlRef.current = "";
    accCssRef.current  = "";
    accJsRef.current   = "";
  };

  const handleAnalyzeDesign = async () => {
    if (!uploadedFileBase64) return;
    setDesignStep("analyzing");
    try {
      const requestUrl =
        url.API_BASE_URL +
        url.API_ANALYZE_WEBSITE_PAGE_DESIGN +
        `/${id}/analyze-design?token=${token}`;
      const response = await postData(requestUrl, { image: uploadedFileBase64 });
      if (response.status && response.data.sections.length > 0) {
        const sections = response.data.sections.map((s) => ({
          ...s,
          selected: true,
          status: "pending",
        }));
        setAnalyzedSections(sections);
        setDesignStep("sections");
      } else {
        setAlertMessage(response.message || t("Could not identify sections in the design"));
        setMessageType("error");
        setShowAlert(true);
        setDesignStep("upload");
      }
    } catch (error) {
      setAlertMessage(error.message);
      setMessageType("error");
      setShowAlert(true);
      setDesignStep("upload");
    }
  };

  const handleBuildPage = async () => {
    const selected = analyzedSections.filter((s) => s.selected);
    if (selected.length === 0) return;

    setDesignStep("building");
    accHtmlRef.current = "";
    accCssRef.current  = "";
    accJsRef.current   = "";

    const buildStart = Date.now();
    const sectionLogs = []; // [{order, name, status, durationMs}]

    for (let i = 0; i < selected.length; i++) {
      const section = selected[i];
      setBuildingIndex(i);
      setAnalyzedSections((prev) =>
        prev.map((s) => (s.order === section.order ? { ...s, status: "generating", durationMs: 0 } : s))
      );

      const sectionStart = Date.now();
      try {
        // Pass :root CSS variables from earlier sections for color consistency
        const rootMatch = accCssRef.current.match(/:root\s*\{[^}]*\}/);
        const existingCssVars = rootMatch ? rootMatch[0] : "";

        const requestUrl =
          url.API_BASE_URL +
          url.API_GENERATE_WEBSITE_PAGE_SECTION +
          `/${id}/generate-section?token=${token}`;

        const response = await postData(requestUrl, {
          image: uploadedFileBase64,
          sectionName: section.name,
          sectionDescription: section.description,
          existingCssVars,
        });

        const durationMs = Date.now() - sectionStart;

        if (response.status) {
          // Wrap with section name comments so AI/user can reference by name in future edits
          const sHtml = `<!-- SECTION: ${section.name} -->\n${response.data.html}\n<!-- /SECTION: ${section.name} -->`;
          const sCss  = `/* SECTION: ${section.name} */\n${response.data.css}\n/* /SECTION: ${section.name} */`;
          const sJs   = response.data.js ? `/* SECTION: ${section.name} */\n${response.data.js}\n/* /SECTION: ${section.name} */` : "";
          accHtmlRef.current += (accHtmlRef.current ? "\n\n" : "") + sHtml;
          accCssRef.current  += (accCssRef.current  ? "\n\n" : "") + sCss;
          accJsRef.current   += (accJsRef.current   ? "\n\n" : "") + sJs;

          // Update live preview after each section
          setHtml(accHtmlRef.current);
          setCss(accCssRef.current);
          setJs(accJsRef.current);
          setActiveRightTab("preview");

          setAnalyzedSections((prev) =>
            prev.map((s) => (s.order === section.order ? { ...s, status: "done", durationMs } : s))
          );
          sectionLogs.push({ order: section.order, name: section.name, status: "done", durationMs });
        } else {
          setAnalyzedSections((prev) =>
            prev.map((s) => (s.order === section.order ? { ...s, status: "error", durationMs } : s))
          );
          sectionLogs.push({ order: section.order, name: section.name, status: "error", durationMs });
        }
      } catch (_err) {
        const durationMs = Date.now() - sectionStart;
        setAnalyzedSections((prev) =>
          prev.map((s) => (s.order === section.order ? { ...s, status: "error", durationMs } : s))
        );
        sectionLogs.push({ order: section.order, name: section.name, status: "error", durationMs });
      }
    }

    // Build the log to persist
    const totalDurationMs = Date.now() - buildStart;
    const designBuildLog = {
      totalSections: selected.length,
      builtSections: sectionLogs.filter((s) => s.status === "done").length,
      totalDurationMs,
      sections: sectionLogs,
    };

    // Build the chat summary entry
    const doneSections  = sectionLogs.filter((s) => s.status === "done");
    const failSections  = sectionLogs.filter((s) => s.status === "error");
    const sectionLines  = doneSections.map((s) => `  • ${s.name} (${formatDuration(s.durationMs)})`).join("\n");
    const buildSummary  =
      `Built page from design image in ${formatDuration(totalDurationMs)}\n` +
      `${doneSections.length} sections built:\n${sectionLines}` +
      (failSections.length > 0 ? `\n${failSections.length} section(s) failed: ${failSections.map((s) => s.name).join(", ")}` : "");

    const newChatHistory = [
      ...chatHistory,
      { role: "user", content: "Build page from uploaded design", timestamp: new Date() },
      { role: "assistant", content: buildSummary, timestamp: new Date() },
    ];

    // Auto-save accumulated result + build log + chatHistory to DB
    try {
      const saveUrl =
        url.API_BASE_URL +
        url.API_SAVE_WEBSITE_PAGE_CODE +
        `/${id}/save?token=${token}`;
      const saveResp = await postData(saveUrl, {
        html: accHtmlRef.current,
        css: accCssRef.current,
        js: accJsRef.current,
        designBuildLog,
        chatHistory: newChatHistory,
      });
      if (saveResp.status) setVersions(saveResp.data.versions);
    } catch (_) {}

    setChatHistory(newChatHistory);

    setBuildingIndex(-1);
    resetDesignFlow();
    setInputMode("chat");
    setAlertMessage(t("Page built successfully! Refine it using the Chat tab."));
    setMessageType("success");
    setShowAlert(true);
  };

  // ── Version rollback ──────────────────────────────────────────────────────
  const handleRollback = async (versionIndex) => {
    try {
      const requestUrl =
        url.API_BASE_URL +
        url.API_ROLLBACK_WEBSITE_PAGE_VERSION +
        `/${id}/rollback?token=${token}`;

      const response = await postData(requestUrl, { versionIndex });

      if (response.status) {
        setHtml(response.data.html);
        setCss(response.data.css);
        setJs(response.data.js);
        setAlertMessage(t("Restored successfully"));
        setMessageType("success");
        setShowAlert(true);
        setActiveRightTab("preview");
      }
    } catch (error) {
      setAlertMessage(error.message);
      setMessageType("error");
      setShowAlert(true);
    }
  };

  // ── Publish / unpublish ───────────────────────────────────────────────────
  const handlePublish = async () => {
    try {
      const requestUrl =
        url.API_BASE_URL +
        url.API_PUBLISH_WEBSITE_PAGE +
        `/${id}/publish?token=${token}`;

      const response = await postData(requestUrl, {});

      if (response.status) {
        setPageStatus(response.data.status);
        setAlertMessage(response.message);
        setMessageType("success");
        setShowAlert(true);
      }
    } catch (error) {
      setAlertMessage(error.message);
      setMessageType("error");
      setShowAlert(true);
    }
  };

  // ── Direct edit mode (WYSIWYG) ────────────────────────────────────────────
  const handleEnterEditMode = () => {
    setActiveRightTab("preview");
    setIsEditMode(true);
    // Make iframe body contenteditable after it renders
    setTimeout(() => {
      const doc = iframeRef.current && iframeRef.current.contentDocument;
      if (doc && doc.body) {
        doc.body.contentEditable = "true";
        doc.body.style.outline = "2px dashed #3b4bf9";
        doc.body.style.outlineOffset = "-2px";
        doc.body.style.cursor = "text";
      }
    }, 150);
  };

  const handleDiscardEdit = () => {
    setIsEditMode(false);
    setIframeKey((k) => k + 1); // remount iframe from original html state
  };

  const handleSaveEditedHtml = async () => {
    const doc = iframeRef.current && iframeRef.current.contentDocument;
    if (!doc || !doc.body) return;

    // Strip the edit-mode styles before saving
    doc.body.contentEditable = "false";
    doc.body.style.outline = "";
    doc.body.style.outlineOffset = "";
    doc.body.style.cursor = "";

    const newHtml = doc.body.innerHTML;

    try {
      const requestUrl =
        url.API_BASE_URL +
        url.API_SAVE_WEBSITE_PAGE_CODE +
        `/${id}/save?token=${token}`;

      const response = await postData(requestUrl, { html: newHtml, css, js });

      if (response.status) {
        setHtml(newHtml);
        setVersions(response.data.versions);
        setIsEditMode(false);
        setIframeKey((k) => k + 1);
        setAlertMessage(t("Changes saved successfully"));
        setMessageType("success");
        setShowAlert(true);
      } else {
        setAlertMessage(response.message || t("Save failed"));
        setMessageType("error");
        setShowAlert(true);
      }
    } catch (error) {
      setAlertMessage(error.message);
      setMessageType("error");
      setShowAlert(true);
    }
  };

  // ── Open preview in new tab ───────────────────────────────────────────────
  const openPreviewInNewTab = () => {
    const doc = buildPreviewDocument(html, css, js);
    const blob = new Blob([doc], { type: "text/html" });
    const blobUrl = URL.createObjectURL(blob);
    window.open(blobUrl, "_blank");
  };

  // ── Download current page as index.html ──────────────────────────────────
  const handleDownloadHtml = () => {
    const doc = buildPreviewDocument(html, css, js);
    const blob = new Blob([doc], { type: "text/html" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `${pageSlug || "page"}.html`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  // ── Drive progress message while generating ───────────────────────────────
  useEffect(() => {
    if (!isGenerating) {
      setGeneratingMessage("");
      generatingStartRef.current = null;
      return;
    }
    generatingStartRef.current = Date.now();
    setGeneratingMessage(getProgressMessage(0));

    const interval = setInterval(() => {
      const elapsed = Date.now() - (generatingStartRef.current || Date.now());
      setGeneratingMessage(getProgressMessage(elapsed));
    }, 3000);

    return () => clearInterval(interval);
  }, [isGenerating]);

  // ── Scroll chat to bottom on new messages ─────────────────────────────────
  useEffect(() => {
    if (chatBottomRef.current) {
      chatBottomRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [chatHistory]);

  useEffect(() => {
    if (id) getPageDetails();
  }, [id]);

  return (
    <div id="content_wrapper">
      <section className="bg-white" style={{ minHeight: "100vh" }}>
        {/* ── Top bar ─────────────────────────────────────────────────── */}
        <div className="border-bottom px-3 px-lg-4 py-2 d-flex align-items-center justify-content-between gap-2">
          <div className="d-flex align-items-center gap-3">
            <button
              className="btn btn-sm btn-light border rounded-3"
              onClick={() => history.push("/admin/websitebuilder/list")}
              title={t("Back")}
            >
              <span className="material-symbols-outlined icon-sm">arrow_back</span>
            </button>
            <BreadCrumb breadCrumbText={breadcrumbText} />
          </div>

          <div className="d-flex align-items-center gap-2">
            <span
              className={`badge rounded-pill px-3 py-2 ${
                pageStatus === "published"
                  ? "bg-success-subtle text-success"
                  : "bg-secondary-subtle text-secondary"
              }`}
            >
              {pageStatus === "published" ? t("Published") : t("Draft")}
            </span>

            {/* ── Edit mode controls ────────────────────────────────── */}
            {isEditMode ? (
              <>
                <button
                  className="btn btn-sm btn-success px-3 rounded-3"
                  onClick={handleSaveEditedHtml}
                  title={t("Save your edits")}
                >
                  <span className="material-symbols-outlined icon-sm me-1">save</span>
                  {t("Save")}
                </button>
                <button
                  className="btn btn-sm btn-outline-secondary px-3 rounded-3"
                  onClick={handleDiscardEdit}
                  title={t("Discard changes and go back")}
                >
                  <span className="material-symbols-outlined icon-sm me-1">undo</span>
                  {t("Discard")}
                </button>
              </>
            ) : (
              <>
                {html && (
                  <button
                    className="btn btn-sm btn-outline-primary px-3 rounded-3"
                    onClick={handleEnterEditMode}
                    title={t("Edit text directly in the preview")}
                  >
                    <span className="material-symbols-outlined icon-sm me-1">edit</span>
                    {t("Edit")}
                  </button>
                )}
                {html && (
                  <button
                    className="btn btn-sm btn-outline-success px-3 rounded-3"
                    onClick={handleDownloadHtml}
                    title={t("Download current page as HTML file")}
                  >
                    <span className="material-symbols-outlined icon-sm me-1">download</span>
                    {t("Download")}
                  </button>
                )}
                <button
                  className={`btn btn-sm px-3 rounded-3 ${
                    pageStatus === "published" ? "btn-outline-secondary" : "btn-primary"
                  }`}
                  onClick={handlePublish}
                >
                  <span className="material-symbols-outlined icon-sm me-1">publish</span>
                  {pageStatus === "published" ? t("Unpublish") : t("Publish")}
                </button>
                {/* Help button */}
                <button
                  className="btn btn-sm btn-outline-secondary rounded-3 px-3"
                  data-bs-toggle="offcanvas"
                  data-bs-target="#wbHelpOffcanvas"
                  title={t("Help")}
                >
                  <span className="material-symbols-outlined icon-sm me-1">help</span>
                  {t("Help")}
                </button>
              </>
            )}
          </div>
        </div>

        {/* ── Split layout ────────────────────────────────────────────── */}
        <div className="d-flex" style={{ height: "calc(100vh - 110px)" }}>

          {/* ═══════════════════════════════════════════════════════════
              LEFT PANEL (40%)
          ═══════════════════════════════════════════════════════════ */}
          <div
            className="border-end d-flex flex-column"
            style={{ width: "40%", minWidth: 320, overflowY: "hidden" }}
          >
            {/* ── Mode switcher ──────────────────────────────────────── */}
            <div className="border-bottom px-3 pt-3 pb-0">
              <ul className="nav nav-tabs border-0">
                <li className="nav-item">
                  <button
                    className={`nav-link px-3 py-2 small fw-semibold border-0 ${
                      inputMode === "chat" ? "active" : "text-muted"
                    }`}
                    onClick={() => setInputMode("chat")}
                  >
                    <span className="material-symbols-outlined icon-sm me-1">chat</span>
                    {t("Chat")}
                  </button>
                </li>
                <li className="nav-item">
                  <button
                    className={`nav-link px-3 py-2 small fw-semibold border-0 ${
                      inputMode === "upload" ? "active" : "text-muted"
                    }`}
                    onClick={() => setInputMode("upload")}
                  >
                    <span className="material-symbols-outlined icon-sm me-1">image</span>
                    {t("Upload Design")}
                  </button>
                </li>
              </ul>
            </div>

            {/* ══════════════════════════════════════════════════════════
                CHAT MODE
            ══════════════════════════════════════════════════════════ */}
            {inputMode === "chat" && (
              <div className="d-flex flex-column flex-grow-1 overflow-hidden">
                {/* Chat history */}
                <div
                  className="flex-grow-1 overflow-y-auto px-3 py-3"
                  style={{ overflowY: "auto" }}
                >
                  {chatHistory.length === 0 && (
                    <div className="text-center text-muted py-5">
                      <span
                        className="material-symbols-outlined text-muted mb-2"
                        style={{ fontSize: 40 }}
                      >
                        auto_awesome
                      </span>
                      <p className="small">
                        {t(
                          "Describe what you want to build. Try a template below to get started."
                        )}
                      </p>
                    </div>
                  )}

                  {chatHistory.map((msg, idx) => (
                    <div
                      key={idx}
                      className={`d-flex mb-3 ${
                        msg.role === "user" ? "justify-content-end" : "justify-content-start"
                      }`}
                    >
                      <div
                        className={`px-3 py-2 rounded-3 small ${
                          msg.role === "user"
                            ? "bg-primary text-white"
                            : "bg-light text-dark border"
                        }`}
                        style={{ maxWidth: "80%" }}
                      >
                        {msg.role === "user" ? (
                          msg.content
                        ) : (
                          <span className="text-success">
                            <span className="d-flex align-items-center gap-1 fw-semibold mb-1">
                              <span className="material-symbols-outlined icon-sm">check_circle</span>
                              {t("Done")}
                            </span>
                            <span style={{ whiteSpace: "pre-line", fontSize: "0.78rem" }}>
                              {msg.content}
                            </span>
                          </span>
                        )}
                      </div>
                    </div>
                  ))}

                  {isGenerating && (
                    <div className="d-flex justify-content-start mb-3">
                      <div className="px-3 py-2 rounded-3 small bg-light border" style={{ maxWidth: "85%" }}>
                        <div className="d-flex align-items-center gap-2 mb-1">
                          <div
                            className="spinner-border spinner-border-sm text-primary flex-shrink-0"
                            role="status"
                          />
                          <span className="text-primary fw-semibold">{t("AI is working...")}</span>
                        </div>
                        <p className="text-muted mb-0" style={{ fontSize: "0.78rem" }}>
                          {generatingMessage}
                        </p>
                      </div>
                    </div>
                  )}

                  <div ref={chatBottomRef} />
                </div>

                {/* Templates dropdown */}
                <div className="px-3 pb-2 border-top pt-2">
                  <div className="d-flex align-items-center gap-2 flex-wrap">
                    <div className="dropdown">
                      <button
                        className="btn btn-sm btn-light border rounded-3 d-flex align-items-center gap-1"
                        data-bs-toggle="dropdown"
                      >
                        <span className="material-symbols-outlined icon-sm">auto_awesome</span>
                        <span className="small">{t("Templates")}</span>
                        <span className="material-symbols-outlined icon-sm">expand_more</span>
                      </button>
                      <ul
                        className="dropdown-menu shadow border-0 rounded-3"
                        style={{ minWidth: 280 }}
                      >
                        {TEMPLATES.map((tmpl, i) => (
                          <li key={i}>
                            <button
                              className="dropdown-item d-flex align-items-start gap-2 py-2 small"
                              onClick={() => handleTemplateSelect(tmpl.label)}
                            >
                              <span className="material-symbols-outlined icon-sm text-primary mt-1">
                                {tmpl.icon}
                              </span>
                              {tmpl.label}
                            </button>
                          </li>
                        ))}
                      </ul>
                    </div>

                    {/* Quick action buttons */}
                    <button
                      className="btn btn-sm btn-light border rounded-3 small"
                      onClick={() =>
                        handleGenerate("Improve the UI design. Make it more modern, polished, and professional.")
                      }
                      disabled={isGenerating || (!html && !css)}
                      title={t("Improve UI")}
                    >
                      <span className="material-symbols-outlined icon-sm me-1">auto_fix_high</span>
                      {t("Improve UI")}
                    </button>

                    <button
                      className="btn btn-sm btn-light border rounded-3 small"
                      onClick={() =>
                        handleGenerate(
                          "Add smooth CSS animations and transitions to enhance the user experience. Use keyframes for entrance animations."
                        )
                      }
                      disabled={isGenerating || (!html && !css)}
                      title={t("Add Animation")}
                    >
                      <span className="material-symbols-outlined icon-sm me-1">animation</span>
                      {t("Add Animation")}
                    </button>

                    {/* API Panel toggle */}
                    <button
                      className={`btn btn-sm border rounded-3 small ${
                        showApiPanel ? "btn-primary" : "btn-light"
                      }`}
                      onClick={() => setShowApiPanel((v) => !v)}
                      title={t("API Reference")}
                    >
                      <span className="material-symbols-outlined icon-sm me-1">api</span>
                      {t("APIs")}
                      {selectedApiTokens.length > 0 && (
                        <span className="badge bg-white text-primary ms-1 small">
                          {selectedApiTokens.length}
                        </span>
                      )}
                    </button>
                  </div>

                  {/* API Panel */}
                  {showApiPanel && (
                    <div className="mt-2 p-2 bg-light rounded-3 border small">
                      <p className="text-muted mb-2 small fw-semibold">
                        {t("Click a token to include it in the next generation:")}
                      </p>
                      <div className="d-flex flex-wrap gap-1">
                        {API_TOKENS.map((apiItem) => (
                          <div key={apiItem.token}>
                            <button
                              className={`btn btn-xs px-2 py-1 rounded-2 border small ${
                                selectedApiTokens.includes(apiItem.token)
                                  ? "btn-primary"
                                  : "btn-light"
                              }`}
                              style={{ fontSize: "0.7rem" }}
                              onClick={() => handleTokenAppend(apiItem.token)}
                              title={`${apiItem.endpoint} → ${apiItem.schema}`}
                            >
                              {apiItem.token}
                            </button>
                          </div>
                        ))}
                      </div>
                      {selectedApiTokens.length > 0 && (
                        <p className="text-muted mt-1 mb-0" style={{ fontSize: "0.7rem" }}>
                          {selectedApiTokens.length} {t("token(s) will be injected into the next prompt")}
                        </p>
                      )}
                    </div>
                  )}
                </div>

                {/* Prompt input */}
                <div className="px-3 pb-3">
                  <div className="border rounded-3 bg-white shadow-sm">
                    <textarea
                      className="form-control border-0 rounded-top-3 small"
                      rows={3}
                      placeholder={t(
                        "Describe what you want to build... (Enter to send, Shift+Enter for newline)"
                      )}
                      value={prompt}
                      onChange={(e) => setPrompt(e.target.value)}
                      onKeyDown={handlePromptKeyDown}
                      disabled={isGenerating}
                      style={{ resize: "none", boxShadow: "none" }}
                    />
                    <div className="d-flex justify-content-end p-2 border-top">
                      <button
                        className="btn btn-primary btn-sm rounded-3 px-3"
                        onClick={() => handleGenerate()}
                        disabled={isGenerating || !prompt.trim()}
                      >
                        {isGenerating ? (
                          <span
                            className="spinner-border spinner-border-sm"
                            role="status"
                          />
                        ) : (
                          <span className="material-symbols-outlined icon-sm">send</span>
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* ══════════════════════════════════════════════════════════
                UPLOAD DESIGN MODE — multi-step pipeline
            ══════════════════════════════════════════════════════════ */}
            {inputMode === "upload" && (
              <div className="d-flex flex-column flex-grow-1 overflow-y-auto px-3 py-3">

                {/* ── Step indicator ────────────────────────────────── */}
                <div className="d-flex align-items-center gap-2 mb-3 small">
                  {["upload", "sections", "building"].map((step, idx) => {
                    const labels = [t("Upload"), t("Sections"), t("Build")];
                    const active = designStep === step || (designStep === "analyzing" && step === "upload");
                    const done =
                      (step === "upload" && (designStep === "sections" || designStep === "building")) ||
                      (step === "sections" && designStep === "building");
                    return (
                      <React.Fragment key={step}>
                        <span
                          className={`badge rounded-pill px-2 py-1 ${
                            done ? "bg-success" : active ? "bg-primary" : "bg-secondary-subtle text-secondary"
                          }`}
                        >
                          {done ? "✓" : idx + 1} {labels[idx]}
                        </span>
                        {idx < 2 && <span className="text-muted">›</span>}
                      </React.Fragment>
                    );
                  })}
                </div>

                {/* ─────────────────────────────────────────────────────
                    STEP 1 — Upload
                ───────────────────────────────────────────────────── */}
                {(designStep === "upload" || designStep === "analyzing") && (
                  <>
                    <div
                      className={`border-2 border-dashed rounded-3 p-4 text-center mb-3 ${
                        isDragging ? "border-primary bg-primary-subtle" : "border-secondary"
                      }`}
                      style={{ borderStyle: "dashed", cursor: "pointer", transition: "all 0.2s" }}
                      onDragOver={handleDragOver}
                      onDragLeave={handleDragLeave}
                      onDrop={handleDrop}
                      onClick={() => fileInputRef.current && fileInputRef.current.click()}
                    >
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept=".png,.jpg,.jpeg,.pdf"
                        className="d-none"
                        onChange={handleFileSelect}
                      />
                      {uploadedFile ? (
                        <div>
                          <span className="material-symbols-outlined text-success" style={{ fontSize: 40 }}>check_circle</span>
                          <p className="fw-semibold small mt-2 mb-0">{uploadedFile.name}</p>
                          <p className="text-muted" style={{ fontSize: "0.7rem" }}>{(uploadedFile.size / 1024).toFixed(1)} KB</p>
                          <button
                            className="btn btn-sm btn-outline-secondary rounded-3"
                            onClick={(e) => { e.stopPropagation(); setUploadedFile(null); setUploadedFileBase64(""); }}
                          >
                            {t("Remove")}
                          </button>
                        </div>
                      ) : (
                        <div>
                          <span className="material-symbols-outlined text-muted" style={{ fontSize: 40 }}>upload_file</span>
                          <p className="small fw-semibold mt-2 mb-1">{t("Drag & drop or click to upload")}</p>
                          <p className="text-muted" style={{ fontSize: "0.7rem" }}>{t("PNG, JPG, JPEG — max 10MB")}</p>
                        </div>
                      )}
                    </div>

                    <button
                      className="btn btn-primary rounded-3 w-100"
                      onClick={handleAnalyzeDesign}
                      disabled={!uploadedFileBase64 || designStep === "analyzing"}
                    >
                      {designStep === "analyzing" ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2" role="status" />
                          {t("Identifying sections...")}
                        </>
                      ) : (
                        <>
                          <span className="material-symbols-outlined icon-sm me-1">document_scanner</span>
                          {t("Analyze Design")}
                        </>
                      )}
                    </button>

                    <p className="text-muted text-center mt-3 small">
                      {t("AI will identify all sections in your design. You then choose which ones to build.")}
                    </p>
                  </>
                )}

                {/* ─────────────────────────────────────────────────────
                    STEP 2 — Confirm sections
                ───────────────────────────────────────────────────── */}
                {designStep === "sections" && (
                  <>
                    <p className="small fw-semibold text-muted mb-2">
                      {t("Found")} {analyzedSections.length} {t("sections — select the ones to build:")}
                    </p>

                    <div className="d-flex flex-column gap-2 mb-3" style={{ maxHeight: 320, overflowY: "auto" }}>
                      {analyzedSections.map((section) => (
                        <div
                          key={section.order}
                          className={`border rounded-3 p-2 d-flex align-items-start gap-2 ${
                            section.selected ? "border-primary bg-primary-subtle" : "border-secondary"
                          }`}
                          style={{ cursor: "pointer" }}
                          onClick={() =>
                            setAnalyzedSections((prev) =>
                              prev.map((s) =>
                                s.order === section.order ? { ...s, selected: !s.selected } : s
                              )
                            )
                          }
                        >
                          <input
                            type="checkbox"
                            className="form-check-input mt-1 flex-shrink-0"
                            checked={section.selected}
                            onChange={() => {}}
                          />
                          <div>
                            <p className="small fw-semibold mb-0">
                              {section.order}. {section.name}
                            </p>
                            <p className="text-muted mb-0" style={{ fontSize: "0.72rem" }}>
                              {section.description.slice(0, 120)}
                              {section.description.length > 120 ? "…" : ""}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>

                    <button
                      className="btn btn-primary rounded-3 w-100 mb-2"
                      onClick={handleBuildPage}
                      disabled={!analyzedSections.some((s) => s.selected)}
                    >
                      <span className="material-symbols-outlined icon-sm me-1">construction</span>
                      {t("Build Page")} ({analyzedSections.filter((s) => s.selected).length} {t("sections")})
                    </button>
                    <button
                      className="btn btn-outline-secondary rounded-3 w-100 small"
                      onClick={resetDesignFlow}
                    >
                      {t("← Start Over")}
                    </button>
                  </>
                )}

                {/* ─────────────────────────────────────────────────────
                    STEP 3 — Building progress
                ───────────────────────────────────────────────────── */}
                {designStep === "building" && (
                  <>
                    <p className="small fw-semibold mb-2">
                      {t("Building")} {buildingIndex + 1} / {analyzedSections.filter((s) => s.selected).length}
                      {buildingIndex >= 0 && (
                        <span className="text-primary ms-1">
                          — {analyzedSections.filter((s) => s.selected)[buildingIndex]?.name}
                        </span>
                      )}
                    </p>

                    <div className="progress mb-3" style={{ height: 6 }}>
                      <div
                        className="progress-bar progress-bar-striped progress-bar-animated"
                        style={{
                          width: `${Math.round(
                            (analyzedSections.filter((s) => s.status === "done").length /
                              Math.max(analyzedSections.filter((s) => s.selected).length, 1)) *
                              100
                          )}%`,
                        }}
                      />
                    </div>

                    <div className="d-flex flex-column gap-2" style={{ maxHeight: 360, overflowY: "auto" }}>
                      {analyzedSections.filter((s) => s.selected).map((section) => (
                        <div
                          key={section.order}
                          className="d-flex align-items-center gap-2 border rounded-3 p-2 small"
                        >
                          {section.status === "done" && (
                            <span className="material-symbols-outlined text-success icon-sm">check_circle</span>
                          )}
                          {section.status === "generating" && (
                            <span className="spinner-border spinner-border-sm text-primary flex-shrink-0" />
                          )}
                          {section.status === "pending" && (
                            <span className="material-symbols-outlined text-muted icon-sm">radio_button_unchecked</span>
                          )}
                          {section.status === "error" && (
                            <span className="material-symbols-outlined text-danger icon-sm">error</span>
                          )}
                          <span
                            className={`flex-grow-1 ${
                              section.status === "done"
                                ? "text-success fw-semibold"
                                : section.status === "generating"
                                ? "text-primary fw-semibold"
                                : section.status === "error"
                                ? "text-danger"
                                : "text-muted"
                            }`}
                          >
                            {section.name}
                          </span>
                          {(section.status === "done" || section.status === "error") && section.durationMs > 0 && (
                            <span className="text-muted flex-shrink-0" style={{ fontSize: "0.7rem" }}>
                              {formatDuration(section.durationMs)}
                            </span>
                          )}
                          {section.status === "generating" && (
                            <span className="text-primary flex-shrink-0" style={{ fontSize: "0.7rem" }}>
                              …
                            </span>
                          )}
                        </div>
                      ))}
                    </div>

                    <p className="text-muted text-center mt-3 small">
                      {t("Preview updates live as each section is built →")}
                    </p>
                  </>
                )}
              </div>
            )}
          </div>

          {/* ═══════════════════════════════════════════════════════════
              RIGHT PANEL (60%)
          ═══════════════════════════════════════════════════════════ */}
          <div className="d-flex flex-column flex-grow-1 overflow-hidden">
            {/* Tab bar + actions */}
            <div className="border-bottom px-3 py-2 d-flex align-items-center justify-content-between">
              <ul className="nav nav-tabs border-0">
                {["preview", "html", "css", "js"].map((tab) => (
                  <li className="nav-item" key={tab}>
                    <button
                      className={`nav-link px-3 py-2 small fw-semibold border-0 text-capitalize ${
                        activeRightTab === tab ? "active" : "text-muted"
                      }`}
                      onClick={() => setActiveRightTab(tab)}
                    >
                      {tab.toUpperCase()}
                    </button>
                  </li>
                ))}
              </ul>

              <div className="d-flex align-items-center gap-2">
                {/* Version history */}
                {versions.length > 0 && (
                  <div className="dropdown">
                    <button
                      className="btn btn-sm btn-light border rounded-3 d-flex align-items-center gap-1"
                      data-bs-toggle="dropdown"
                    >
                      <span className="material-symbols-outlined icon-sm">history</span>
                      <span className="small">{t("History")}</span>
                      <span className="badge bg-secondary ms-1">{versions.length}</span>
                    </button>
                    <ul
                      className="dropdown-menu dropdown-menu-end shadow border-0 rounded-3"
                      style={{ minWidth: 220 }}
                    >
                      {versions.map((v, idx) => (
                        <li key={idx}>
                          <button
                            className="dropdown-item d-flex align-items-center justify-content-between py-2 small"
                            onClick={() => handleRollback(idx)}
                          >
                            <span>
                              {t("Version")} {idx + 1}
                              <span className="text-muted ms-1" style={{ fontSize: "0.7rem" }}>
                                {new Date(v.createdAt).toLocaleString()}
                              </span>
                            </span>
                            <span className="material-symbols-outlined icon-sm text-primary">
                              restore
                            </span>
                          </button>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                <button
                  className="btn btn-sm btn-light border rounded-3"
                  onClick={openPreviewInNewTab}
                  title={t("Open in new tab")}
                  disabled={!html}
                >
                  <span className="material-symbols-outlined icon-sm">open_in_new</span>
                </button>
                <button
                  className="btn btn-sm btn-light border rounded-3"
                  onClick={handleDownloadHtml}
                  title={t("Download as HTML file")}
                  disabled={!html}
                >
                  <span className="material-symbols-outlined icon-sm">download</span>
                </button>
              </div>
            </div>

            {/* Content area */}
            <div className="flex-grow-1 overflow-hidden position-relative">
              {/* Preview iframe */}
              {activeRightTab === "preview" && (
                <div className="w-100 h-100">
                  {html || css ? (
                    <iframe
                      key={iframeKey}
                      ref={iframeRef}
                      title="Page Preview"
                      sandbox="allow-scripts allow-same-origin"
                      className="w-100 h-100 border-0"
                      srcDoc={buildPreviewDocument(html, css, js)}
                    />
                  ) : (
                    <div className="d-flex flex-column align-items-center justify-content-center h-100 text-muted">
                      <span className="material-symbols-outlined mb-3" style={{ fontSize: 64 }}>
                        web
                      </span>
                      <p className="small">{t("Your page preview will appear here after generation.")}</p>
                    </div>
                  )}
                </div>
              )}

              {/* Code tabs */}
              {activeRightTab !== "preview" && (
                <pre
                  className="m-0 h-100 overflow-auto p-3 small"
                  style={{
                    background: "#1e1e1e",
                    color: "#d4d4d4",
                    fontFamily: "monospace",
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-word",
                  }}
                >
                  {activeRightTab === "html" && (html || t("// No HTML generated yet"))}
                  {activeRightTab === "css" && (css || t("/* No CSS generated yet */"))}
                  {activeRightTab === "js" && (js || t("// No JS generated yet"))}
                </pre>
              )}
            </div>
          </div>
        </div>
      </section>

      {showAlert && (
        <AlertNotification
          showAlert={showAlert}
          message={alertMessage}
          alertType={messageType}
          onClose={() => setShowAlert(false)}
        />
      )}

      {/* ── Help Offcanvas ───────────────────────────────────────────────── */}
      <WbHelpOffcanvas
        companycif={pageCompanyCif}
        slug={pageSlug}
        apiBaseUrl={pageApiBaseUrl || url.API_BASE_URL}
      />
    </div>
  );
};

export default PageEditorBody;
